import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiArrowLeft,
  FiCheck, FiXCircle, FiEye, FiCreditCard, FiPrinter, FiRefreshCw, FiCornerDownLeft
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { recomputeRecoveryFromLivePayrolls } from '../../utils/payrollHelper';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton,
  LoadingMessage, TranEmptyState, TableContainer, Table, Th, Tr, Td,
  StatusBadge, ActionGroup, IconBtn,
  FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea
} from '../../components/SharedStyles';
import {
  createTransaction, updateTransaction, deleteTransaction,
  getTransactions, updateTransactionStatus
} from '../../utils/transactionHelper';
import { printDocument, loadDocumentFormats } from '../../utils/printTemplates';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useCurrency from '../../hooks/useCurrency';
import useOpenFromReport from '../../hooks/useOpenFromReport';
import { isFutureDate, isLargeTransaction, LARGE_TRANSACTION_THRESHOLD } from '../../utils/validationHelpers';
import { formatAmount } from '../../utils/documentNumbering';

const TRAN_TYPE_CODE = 'LON';
const STATUS_COLORS = {
  Draft: colors.warning.main,
  Active: colors.info.main,
  Closed: colors.success.main,
  Cancelled: colors.error.main,
};

const monthInputDefault = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);  // default to next month
  return d.toISOString().slice(0, 7); // YYYY-MM
};

const SalaryLoan = () => {
  const { activeCompany, activeFinancialYear, currentUser, activeBranch, isModuleEnabled } = useAuth();
  const { canEdit, canDelete } = usePagePermissions('salary', 'loan');
  // Loan paid back in cash / bank → opens a Loan Recovery voucher for it.
  const { canEdit: canRecover } = usePagePermissions('salary', 'loanRecovery');
  const recoveryOn = !!(isModuleEnabled && isModuleEnabled('salary_loan_recovery'));
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [viewMode, setViewMode] = useState('list');
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    docDate: new Date().toISOString().split('T')[0],
    partyId: '', partyName: '', partyAccountId: '', partyAccountCode: '',
    principal: 0,
    numberOfInstallments: 12,
    firstInstallmentMonth: monthInputDefault(),
    paymentMethod: 'bank',
    paymentAccountId: '', paymentAccountCode: '', paymentAccountName: '',
    purpose: '', notes: '',
    status: 'Draft'
  });

  const [staffList, setStaffList] = useState([]);
  const [cashAccounts, setCashAccounts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  // Print config (per-company document format overrides)
  const [printConfig, setPrintConfig] = useState(null);
  useEffect(() => {
    if (activeCompany?.id) loadDocumentFormats(activeCompany.id).then(setPrintConfig);
  }, [activeCompany]);

  const handlePrint = (lon) => {
    printDocument('staffLoan', lon, activeCompany, printConfig, activeBranch);
  };

  // ─── Load data ───
  const loadLoans = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const txs = await getTransactions(activeCompany.id, {
        tranTypeCode: TRAN_TYPE_CODE,
        ...(activeBranch && activeBranch.id !== 'all' ? { branchId: activeBranch.id } : {})
      });
      setLoans(txs);
    } catch (e) { console.error('Load LON error:', e); }
    finally { setLoading(false); }
  }, [activeCompany, activeBranch]);

  const loadLookups = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const [staffSnap, accSnap] = await Promise.all([
        // Load all staff, filter client-side (avoids needing a composite index on isActive+staffCode)
        getDocs(query(
          collection(db, 'companies', activeCompany.id, 'salaryStaff'),
          orderBy('staffCode')
        )),
        getDocs(collection(db, 'companies', activeCompany.id, 'accounts'))
      ]);
      setStaffList(
        staffSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(s => s.isActive !== false)
      );

      const allAccs = accSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setCashAccounts(allAccs.filter(a => a.level === 5 && a.code?.startsWith('11101') && a.isActive !== false)
        .sort((a, b) => (a.code || '').localeCompare(b.code || '')));
      setBankAccounts(allAccs.filter(a => a.level === 5 && a.code?.startsWith('11102') && a.isActive !== false)
        .sort((a, b) => (a.code || '').localeCompare(b.code || '')));
    } catch (e) { console.error('Load lookups error:', e); }
  }, [activeCompany]);

  useEffect(() => { loadLoans(); loadLookups(); }, [loadLoans, loadLookups]);

  // ─── Live computation ───
  const calc = useMemo(() => {
    const principal = Number(formData.principal) || 0;
    const n = Math.max(1, parseInt(formData.numberOfInstallments) || 1);
    const monthly = Math.round((principal / n) * 100) / 100;
    // Last installment absorbs rounding drift so total matches principal exactly
    const lastInstallment = Math.round((principal - (monthly * (n - 1))) * 100) / 100;
    return { principal, n, monthly, lastInstallment, hasDrift: monthly !== lastInstallment };
  }, [formData.principal, formData.numberOfInstallments]);

  // ─── Form helpers ───
  const resetForm = () => {
    setEditing(null);
    setFormData({
      docDate: new Date().toISOString().split('T')[0],
      partyId: '', partyName: '', partyAccountId: '', partyAccountCode: '',
      principal: 0,
      numberOfInstallments: 12,
      firstInstallmentMonth: monthInputDefault(),
      paymentMethod: 'bank',
      paymentAccountId: '', paymentAccountCode: '', paymentAccountName: '',
      purpose: '', notes: '',
      status: 'Draft'
    });
  };

  const openNew = () => {
    if (!activeFinancialYear?.id) { alert('Activate a financial year first.'); return; }
    if (activeBranch && activeBranch.id === 'all') {
      alert('Pick a specific branch before creating a Loan.');
      return;
    }
    resetForm();
    setViewMode('form');
  };

  const openEdit = (lon) => {
    setEditing(lon);
    setFormData({
      docDate: lon.docDate || '',
      partyId: lon.partyId || '',
      partyName: lon.partyName || '',
      partyAccountId: lon.partyAccountId || '',
      partyAccountCode: lon.partyAccountCode || '',
      principal: lon.totalAmount || 0,
      numberOfInstallments: lon.numberOfInstallments || 12,
      firstInstallmentMonth: lon.firstInstallmentMonth || monthInputDefault(),
      paymentMethod: lon.paymentMethod || 'bank',
      paymentAccountId: lon.paymentAccountId || '',
      paymentAccountCode: lon.paymentAccountCode || '',
      paymentAccountName: lon.paymentAccountName || '',
      purpose: lon.purpose || '', notes: lon.notes || '',
      status: lon.status || 'Draft'
    });
    setViewMode('form');
  };

  const goBack = () => { setViewMode('list'); resetForm(); };

  useOpenFromReport(loans, openEdit);

  const handleStaffChange = (staffId) => {
    const s = staffList.find(x => x.id === staffId);
    setFormData(prev => ({
      ...prev,
      partyId: staffId,
      partyName: s?.fullName || '',
      partyAccountId: s?.linkedAccountId || '',
      partyAccountCode: ''
    }));
  };

  const handleAccountChange = (accountId) => {
    const list = formData.paymentMethod === 'cash' ? cashAccounts : bankAccounts;
    const acc = list.find(a => a.id === accountId);
    setFormData(prev => ({
      ...prev,
      paymentAccountId: accountId,
      paymentAccountCode: acc?.code || '',
      paymentAccountName: acc?.name || ''
    }));
  };

  const handlePaymentMethodChange = (method) => {
    setFormData(prev => ({
      ...prev,
      paymentMethod: method,
      paymentAccountId: '', paymentAccountCode: '', paymentAccountName: ''
    }));
  };

  // ─── Validation + Save ───
  const validate = () => {
    if (!formData.partyId) return 'Select a Salary Staff member.';
    if (!formData.partyAccountId) return 'Selected staff has no linked COA account. Edit the staff record and re-save.';
    if (calc.principal <= 0) return 'Principal amount must be greater than zero.';
    if (calc.n < 1 || calc.n > 60) return 'Number of installments must be between 1 and 60.';
    if (!formData.firstInstallmentMonth) return 'Pick the first installment month.';
    if (!formData.paymentAccountId) return 'Select a payment account (Cash or Bank).';
    return null;
  };

  const buildTransactionData = (status) => ({
    tranTypeCode: TRAN_TYPE_CODE,
    docDate: formData.docDate,
    financialYearId: activeFinancialYear?.id,
    branchId: activeBranch?.id || null,
    branchName: activeBranch?.branchName || null,
    partyType: 'staff',
    partyId: formData.partyId,
    partyName: formData.partyName,
    partyAccountId: formData.partyAccountId,
    partyAccountCode: formData.partyAccountCode,
    paymentMethod: formData.paymentMethod,
    paymentAccountId: formData.paymentAccountId,
    paymentAccountCode: formData.paymentAccountCode,
    paymentAccountName: formData.paymentAccountName,
    purpose: formData.purpose,
    notes: formData.notes,
    // Loan-specific
    numberOfInstallments: calc.n,
    monthlyInstallment: calc.monthly,
    lastInstallment: calc.lastInstallment,
    firstInstallmentMonth: formData.firstInstallmentMonth,
    paidInstallments: editing?.paidInstallments || 0,
    // Standard totals (no lines, no tax)
    subtotal: calc.principal,
    discountAmount: 0,
    taxableAmount: calc.principal,
    taxAmount: 0,
    whtRate: 0, whtAmount: 0,
    totalAmount: calc.principal,
    recoveredAmount: editing?.recoveredAmount || 0,
    outstandingAmount: calc.principal - (editing?.recoveredAmount || 0),
    status
  });

  const runWarnings = (status) => {
    if (isFutureDate(formData.docDate)) {
      if (!window.confirm('Document date is in the future. Continue?')) return false;
    }
    if (isLargeTransaction(calc.principal)) {
      if (!window.confirm(`Principal Rs ${formatAmount(calc.principal)} exceeds Rs ${formatAmount(LARGE_TRANSACTION_THRESHOLD)}. Continue?`)) return false;
    }
    // Soft-warn: existing active loan for this staff
    if (status === 'Active' && formData.partyId) {
      const existing = loans.find(l =>
        l.partyId === formData.partyId &&
        l.id !== editing?.id &&
        l.status === 'Active'
      );
      if (existing) {
        if (!window.confirm(`Staff already has an active loan ${existing.docNumber} (outstanding Rs ${formatAmount(existing.outstandingAmount || 0)}). Continue with another loan?`)) return false;
      }
    }
    return true;
  };

  const handleSave = async (status = 'Draft') => {
    if (!canEdit) { alert('No permission to save.'); return; }
    const err = validate();
    if (err) { alert(err); return; }
    if (!runWarnings(status)) return;

    setSaving(true);
    try {
      const data = buildTransactionData(status);
      let newTranData = null;
      if (editing) {
        await updateTransaction(activeCompany.id, editing.id, data, currentUser.uid);
      } else {
        newTranData = await createTransaction(activeCompany.id, data, currentUser.uid);
      }

      // Optimistic UI update — show the saved record immediately so the Save
      // button releases without waiting on a full collection re-fetch.
      const savedId = editing ? editing.id : newTranData?.id;
      const savedRecord = editing
        ? { ...editing, ...data, id: savedId, status }
        : { ...newTranData, status };
      setLoans(prev => editing
        ? prev.map(r => r.id === savedId ? savedRecord : r)
        : [savedRecord, ...prev]);

      goBack();

      // Background refresh — reconciles with server data without blocking UI.
      loadLoans().catch(err => console.error('Background refresh failed:', err));
    } catch (e) {
      console.error('Save LON error:', e);
      alert('Failed: ' + (e?.message || ''));
    } finally { setSaving(false); }
  };

  const handleStatusChange = async (lon, newStatus) => {
    if (!canEdit) { alert('No permission.'); return; }
    if (!window.confirm(`Change ${lon.docNumber} status to "${newStatus}"?`)) return;
    try {
      await updateTransactionStatus(activeCompany.id, lon.id, newStatus, currentUser.uid);
      await loadLoans();
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  const handleDelete = async (lon) => {
    if (!canDelete) { alert('No permission.'); return; }
    if ((lon.recoveredAmount || 0) > 0) {
      alert(`Cannot delete: ${formatCurrency(lon.recoveredAmount)} has been recovered. Cancel instead.`);
      return;
    }
    if (!window.confirm(`Delete ${lon.docNumber}? This removes the journal permanently.`)) return;
    try {
      await deleteTransaction(activeCompany.id, lon.id);
      await loadLoans();
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  // Recalculate a stuck loan's recovery from the payrolls that still exist. If the
  // recovering payroll was deleted, recovered → 0 and the loan unlocks (Edit/Cancel/
  // Delete come back). Only fixes tracking fields — the ledger stays as-is.
  const handleUnlock = async (lon) => {
    if (!canDelete) { alert('No permission.'); return; }
    if (!window.confirm(`Recalculate recovery for ${lon.docNumber}?\n\nCurrently shows ${formatCurrency(lon.recoveredAmount || 0)} recovered. This resets it from the payrolls and Loan Recovery vouchers that still exist — if none, it unlocks the loan for editing/deleting.`)) return;
    try {
      const res = await recomputeRecoveryFromLivePayrolls(activeCompany.id, lon);
      await loadLoans();
      alert(`Recovery recalculated: now ${formatCurrency(res.recovered)} recovered.`);
    } catch (e) { alert('Failed: ' + (e?.message || '')); }
  };

  useDocumentShortcuts({
    onNew: openNew,
    onSave: () => handleSave(formData.status === 'Active' ? 'Active' : 'Draft'),
    viewMode,
    saving
  });

  const filtered = loans.filter(l => {
    const m = !searchQuery ||
      l.docNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.partyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.purpose?.toLowerCase().includes(searchQuery.toLowerCase());
    const s = statusFilter === 'All' || l.status === statusFilter;
    return m && s;
  });

  // ═══════════════════════════════════════════════════════════
  if (viewMode === 'list') {
    const totalOutstanding = loans
      .filter(l => l.status === 'Active')
      .reduce((sum, l) => sum + (l.outstandingAmount || 0), 0);
    const activeCount = loans.filter(l => l.status === 'Active').length;

    return (
      <PageLayout pageTitle="Staff Loan" breadcrumb="Salary / Loan">
        <ActionBar>
          <SearchBox>
            <FiSearch />
            <input type="text" placeholder="Search doc#, staff, purpose..."
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </SearchBox>
          <FilterGroup>
            <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="All">All Status</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
            </StatusFilterSelect>
          </FilterGroup>
          {canEdit && <TranAddButton onClick={openNew}><FiPlus /> New Loan</TranAddButton>}
        </ActionBar>

        <StatsRow>
          <StatCard>
            <StatLabel>Total Loans</StatLabel>
            <StatValue>{loans.length}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>Active</StatLabel>
            <StatValue>{activeCount}</StatValue>
          </StatCard>
          <StatCard $accent>
            <StatLabel>Total Outstanding (Active)</StatLabel>
            <StatValue>{formatCurrency(totalOutstanding)}</StatValue>
          </StatCard>
        </StatsRow>

        {loading ? <LoadingMessage>Loading loans...</LoadingMessage>
          : filtered.length === 0 ? (
            <TranEmptyState>
              {searchQuery || statusFilter !== 'All'
                ? 'No loans match your filters.'
                : 'No staff loans yet. Click "New Loan" to disburse one.'}
            </TranEmptyState>
          ) : (
            <TableContainer>
              <Table>
                <thead><tr>
                  <Th>Doc #</Th><Th>Date</Th><Th>Staff</Th><Th>Purpose</Th>
                  <Th style={{ textAlign: 'right' }}>Principal</Th>
                  <Th style={{ textAlign: 'center' }}>Installments</Th>
                  <Th style={{ textAlign: 'right' }}>Monthly</Th>
                  <Th style={{ textAlign: 'right' }}>Recovered</Th>
                  <Th style={{ textAlign: 'right' }}>Outstanding</Th>
                  <Th>Disbursed From</Th>
                  {showBranchCol && <Th>Branch</Th>}
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr></thead>
                <tbody>
                  {filtered.map(l => (
                    <Tr key={l.id}>
                      <Td><strong>{l.docNumber}</strong></Td>
                      <Td>{l.docDate}</Td>
                      <Td>{l.partyName || '-'}</Td>
                      <Td style={{ color: colors.text.secondary, fontSize: 12 }}>
                        {l.purpose ? (l.purpose.length > 40 ? l.purpose.slice(0, 40) + '…' : l.purpose) : '-'}
                      </Td>
                      <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(l.totalAmount || 0)}</Td>
                      <Td style={{ textAlign: 'center' }}>
                        <InstBadge>{l.paidInstallments || 0} / {l.numberOfInstallments || 0}</InstBadge>
                      </Td>
                      <Td style={{ textAlign: 'right' }}>{formatCurrency(l.monthlyInstallment || 0)}</Td>
                      <Td style={{ textAlign: 'right', color: colors.success.dark }}>
                        {formatCurrency(l.recoveredAmount || 0)}
                        {(l.voucherRecoveredAmount || 0) > 0 && (
                          <div style={{ fontSize: 10, color: colors.text.secondary, fontWeight: 400 }}>incl. {formatCurrency(l.voucherRecoveredAmount)} by voucher</div>
                        )}
                      </Td>
                      <Td style={{ textAlign: 'right', color: (l.outstandingAmount || 0) > 0 ? colors.warning.dark : colors.text.tertiary, fontWeight: 600 }}>
                        {formatCurrency(l.outstandingAmount ?? l.totalAmount ?? 0)}
                      </Td>
                      <Td>
                        <PayBadge $type={l.paymentMethod}>{(l.paymentMethod || 'bank').toUpperCase()}</PayBadge>
                        <small style={{ marginLeft: 4, color: colors.text.secondary }}>{l.paymentAccountName || ''}</small>
                      </Td>
                      {showBranchCol && <Td>{l.branchName || '-'}</Td>}
                      <Td>
                        <StatusBadge style={{ background: STATUS_COLORS[l.status] + '20', color: STATUS_COLORS[l.status] }}>
                          {l.status}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <ActionGroup>
                          <IconBtn title="View" onClick={() => openEdit(l)}><FiEye /></IconBtn>
                          {canEdit && (l.recoveredAmount || 0) === 0 && (
                            <IconBtn title="Edit" onClick={() => openEdit(l)}><FiEdit2 /></IconBtn>
                          )}
                          {canEdit && l.status === 'Draft' && (
                            <IconBtn title="Disburse (post journal)" onClick={() => handleStatusChange(l, 'Active')}><FiCheck /></IconBtn>
                          )}
                          {canEdit && l.status === 'Active' && (l.recoveredAmount || 0) === 0 && (
                            <IconBtn title="Cancel" onClick={() => handleStatusChange(l, 'Cancelled')}><FiXCircle /></IconBtn>
                          )}
                          {recoveryOn && canRecover && l.status === 'Active' && (l.outstandingAmount ?? l.totalAmount ?? 0) > 0 && (
                            <IconBtn title="Recover — paid back in cash / bank"
                              onClick={() => navigate('/salary/loan-recovery', { state: { recoverFor: { staffId: l.partyId, transactionId: l.id } } })}>
                              <FiCornerDownLeft />
                            </IconBtn>
                          )}
                          <IconBtn title="Print" onClick={() => handlePrint(l)}><FiPrinter /></IconBtn>
                          {canDelete && (l.recoveredAmount || 0) > 0 && (
                            <IconBtn title="Recalculate / unlock recovery" onClick={() => handleUnlock(l)}><FiRefreshCw /></IconBtn>
                          )}
                          {canDelete && (l.recoveredAmount || 0) === 0 && (
                            <IconBtn $danger title="Delete" onClick={() => handleDelete(l)}><FiTrash2 /></IconBtn>
                          )}
                        </ActionGroup>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          )}
      </PageLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════ FORM
  const accountList = formData.paymentMethod === 'cash' ? cashAccounts : bankAccounts;
  const totalCheck = (calc.monthly * (calc.n - 1)) + calc.lastInstallment;

  return (
    <PageLayout pageTitle="Staff Loan" breadcrumb="Salary / Loan">
      <FormHeader>
        <BackButton onClick={goBack}><FiArrowLeft /> Back</BackButton>
        <FormTitle>{editing ? `Edit ${editing.docNumber}` : 'New Staff Loan'}</FormTitle>
        <FormActions>
          {editing && (
            <SaveButton type="button" onClick={() => handlePrint(editing)}
              style={{ background: colors.text.secondary }}>
              <FiPrinter /> Print
            </SaveButton>
          )}
          <SaveButton type="button" onClick={() => handleSave('Draft')} disabled={saving}>
            Save Draft
          </SaveButton>
          <SaveButton type="button" onClick={() => handleSave('Active')} disabled={saving}
            style={{ background: colors.success.main }}>
            {saving ? 'Saving...' : 'Save & Disburse'}
          </SaveButton>
        </FormActions>
      </FormHeader>

      <FormCard>
        <SectionTitle>Loan Details</SectionTitle>
        <FormGrid>
          <FormGroup>
            <Label>Doc Date *</Label>
            <Input type="date" value={formData.docDate} onChange={e => setFormData({...formData, docDate: e.target.value})} />
          </FormGroup>
          <FormGroup>
            <Label>Salary Staff *</Label>
            <Select value={formData.partyId} onChange={e => handleStaffChange(e.target.value)}>
              <option value="">— Select Staff —</option>
              {staffList.map(s => (
                <option key={s.id} value={s.id}>{s.staffCode} — {s.fullName}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup>
            <Label>Principal Amount *</Label>
            <Input type="number" min="0" step="0.01"
              value={formData.principal} onChange={e => setFormData({...formData, principal: e.target.value})} />
          </FormGroup>
          <FormGroup>
            <Label># of Installments *</Label>
            <Input type="number" min="1" max="60" step="1"
              value={formData.numberOfInstallments}
              onChange={e => setFormData({...formData, numberOfInstallments: e.target.value})} />
          </FormGroup>
          <FormGroup>
            <Label>First Installment Month *</Label>
            <Input type="month" value={formData.firstInstallmentMonth}
              onChange={e => setFormData({...formData, firstInstallmentMonth: e.target.value})} />
          </FormGroup>
          <FormGroup>
            <Label>Disburse From</Label>
            <RadioRow>
              <label>
                <input type="radio" name="payMethod" checked={formData.paymentMethod === 'cash'}
                  onChange={() => handlePaymentMethodChange('cash')} /> Cash
              </label>
              <label>
                <input type="radio" name="payMethod" checked={formData.paymentMethod === 'bank'}
                  onChange={() => handlePaymentMethodChange('bank')} /> Bank
              </label>
            </RadioRow>
          </FormGroup>
          <FormGroup style={{ gridColumn: 'span 2' }}>
            <Label>{formData.paymentMethod === 'cash' ? 'Cash Account' : 'Bank Account'} *</Label>
            <Select value={formData.paymentAccountId} onChange={e => handleAccountChange(e.target.value)}>
              <option value="">— Select {formData.paymentMethod === 'cash' ? 'Cash' : 'Bank'} Account —</option>
              {accountList.map(a => (
                <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup style={{ gridColumn: 'span 2' }}>
            <Label>Purpose</Label>
            <Input value={formData.purpose} onChange={e => setFormData({...formData, purpose: e.target.value})}
              placeholder="e.g. House construction, vehicle purchase, education" />
          </FormGroup>
          <FormGroup style={{ gridColumn: 'span 4' }}>
            <Label>Notes</Label>
            <TextArea rows={2} value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} />
          </FormGroup>
        </FormGrid>
      </FormCard>

      {/* Live Repayment Schedule Summary */}
      <ScheduleCard>
        <SectionTitle>Repayment Schedule</SectionTitle>
        <SchGrid>
          <Sch><span>Principal</span><strong>{formatCurrency(calc.principal)}</strong></Sch>
          <Sch><span># Installments</span><strong>{calc.n}</strong></Sch>
          <Sch $highlight><span>Monthly Installment</span><strong>{formatCurrency(calc.monthly)}</strong></Sch>
          {calc.hasDrift && (
            <Sch><span>Last Installment</span><strong>{formatCurrency(calc.lastInstallment)}</strong></Sch>
          )}
          <Sch><span>Total of all installments</span><strong>{formatCurrency(totalCheck)}</strong></Sch>
          <Sch><span>First Installment Month</span><strong>{formData.firstInstallmentMonth || '—'}</strong></Sch>
        </SchGrid>
        {calc.hasDrift && (
          <DriftNote>Note: monthly installment × ({calc.n} − 1) leaves a small remainder; the <strong>last installment</strong> absorbs it so the total exactly matches the principal.</DriftNote>
        )}
      </ScheduleCard>

      <InfoNote>
        <strong>How recovery works:</strong> on <em>Disburse</em>, the system posts <code>Dr Staff Account / Cr {formData.paymentMethod === 'cash' ? 'Cash' : 'Bank'}</code> for the full principal.
        Each Payroll Run starting from <em>{formData.firstInstallmentMonth || 'first installment month'}</em> will deduct one monthly installment from the staff's net salary.
        Status auto-flips to <em>Closed</em> when the last installment is recovered.
      </InfoNote>
    </PageLayout>
  );
};

// ─── Local Styles ───
const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${spacing.md}; margin-bottom: ${spacing.lg};
`;
const StatCard = styled.div`
  background: ${props => props.$accent ? colors.primary.main : colors.background.default};
  color: ${props => props.$accent ? 'white' : colors.text.primary};
  padding: ${spacing.md}; border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.sm};
  border-left: 3px solid ${props => props.$accent ? colors.primary.dark : colors.border.light};
`;
const StatLabel = styled.div`font-size: 11px; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;`;
const StatValue = styled.div`font-size: 22px; font-weight: 700; font-family: monospace;`;

const RadioRow = styled.div`
  display: flex; gap: ${spacing.lg}; align-items: center;
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border.light}; border-radius: ${borderRadius.sm};
  background: ${colors.background.default};
  label { display: flex; align-items: center; gap: 6px; cursor: pointer; font-size: ${typography.fontSize.sm}; }
  input { cursor: pointer; }
`;

const InstBadge = styled.span`
  display: inline-block; padding: 3px 10px;
  background: ${colors.info.main}15; color: ${colors.info.dark};
  border-radius: ${borderRadius.xl}; font-size: 11px; font-weight: 700;
  font-family: monospace;
`;

const PayBadge = styled.span`
  display: inline-block; padding: 2px 8px;
  background: ${props => props.$type === 'cash' ? colors.warning.main + '20' : colors.info.main + '20'};
  color: ${props => props.$type === 'cash' ? colors.warning.dark : colors.info.dark};
  border-radius: ${borderRadius.sm}; font-size: 10px; font-weight: 700;
`;

const ScheduleCard = styled.div`
  background: ${colors.background.default};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.lg};
  margin-top: ${spacing.md};
`;
const SchGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};
`;
const Sch = styled.div`
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.$highlight ? colors.primary.main : colors.neutral[50]};
  color: ${props => props.$highlight ? 'white' : colors.text.primary};
  border-radius: ${borderRadius.sm};
  display: flex; flex-direction: column; gap: 4px;
  span { font-size: 11px; opacity: 0.85; text-transform: uppercase; letter-spacing: 0.5px; }
  strong { font-size: ${typography.fontSize.lg}; font-family: monospace; }
`;
const DriftNote = styled.p`
  margin-top: ${spacing.md};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.warning.main}10;
  border-left: 3px solid ${colors.warning.main};
  border-radius: ${borderRadius.sm};
  font-size: 12px; color: ${colors.text.secondary}; line-height: 1.5;
`;

const InfoNote = styled.div`
  margin-top: ${spacing.md};
  padding: ${spacing.md};
  background: ${colors.info.main}10;
  border-left: 3px solid ${colors.info.main};
  border-radius: ${borderRadius.sm};
  color: ${colors.text.secondary}; font-size: 12px; line-height: 1.6;
  code {
    background: white; padding: 1px 6px; border-radius: 3px;
    font-family: monospace; color: ${colors.primary.main};
  }
`;

export default SalaryLoan;
